class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.fallbackCollection = this.getAttribute('data-fallback-collection') || 'all';

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=4&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const section = parsed.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = section.innerHTML;

        // Check for real product links — if none found, show fallback
        const hasProducts = section.querySelector('.predictive-search__list-item a.predictive-search__item--link') !== null;

        if (!hasProducts) {
          this.getFallbackProducts(searchTerm);
          return;
        }

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  // ── FALLBACK: fetch bestsellers and render in same ul/li structure ────
  getFallbackProducts(searchTerm) {
    const fallbackKey = `__fallback__${this.fallbackCollection}`;

    if (this.cachedResults[fallbackKey]) {
      this.renderFallbackResults(this.cachedResults[fallbackKey], searchTerm);
      return;
    }

    fetch(`/collections/${this.fallbackCollection}/products.json?limit=4`)
      .then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then(({ products }) => {
        this.cachedResults[fallbackKey] = products;
        this.renderFallbackResults(products, searchTerm);
      })
      .catch(() => {
        // If fallback fetch fails, just close
        this.close();
      });
  }

  renderFallbackResults(products, searchTerm) {
    const noResultsTemplate = (window.searchStrings && window.searchStrings.noResultsHtml) || 'No results for "[terms]" — here are some products you might like:';
    const noResultsText = noResultsTemplate.replace('[terms]', searchTerm);
    const markup = `
      <div id="predictive-search-results">
        <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">
          ${noResultsText}
        </h2>
        <ul id="predictive-search-results-list"
            class="predictive-search__results-list list-unstyled"
            role="listbox"
            aria-labelledby="predictive-search-products">
          ${products.map((product, i) => this.buildFallbackItem(product, i)).join('')}
        </ul>
      </div>`;

    this.predictiveSearchResults.innerHTML = markup;
    this.setAttribute('results', true);
    this.removeAttribute('loading');
    this.open();
  }

  buildFallbackItem(product, index) {
    const image = product.images?.[0];
    const imgTag = image
      ? `<img class="predictive-search__image"
              src="${image.src}&width=150"
              alt="${product.title}"
              width="50"
              height="50"
              loading="lazy">`
      : '';

    const formatPrice = (cents) => {
      const amount = (parseFloat(cents)).toFixed(2).replace('.', ',');
      return `€${amount}`;
    };

    // pick the first non-free variant instead of blindly using variants[0]
    const variant = product.variants?.find((v) => parseFloat(v.price) > 0) || product.variants?.[0];
    const price = variant?.price ? formatPrice(variant.price) : '';
    const comparePrice = variant?.compare_at_price
      && parseFloat(variant.compare_at_price) > parseFloat(variant.price)
      ? formatPrice(variant.compare_at_price)
      : null;

    const priceHTML = price ? `
      <div class="price${comparePrice ? ' price--on-sale' : ''}">
        <div class="price__container">
          ${comparePrice ? `
          <div class="price__sale">
            <span class="visually-hidden visually-hidden--inline">Aanbiedingsprijs</span>
            <span class="price-item price-item--sale price-item--last">${price}</span>
            <span><s class="price-item price-item--regular">${comparePrice}</s></span>
          </div>` : `
          <div class="price__regular">
            <span class="price-item price-item--regular">${price}</span>
          </div>`}
        </div>
      </div>` : '';

    return `
      <li id="predictive-search-fallback-${index}"
          class="predictive-search__list-item"
          role="option"
          aria-selected="false">
        <a href="/products/${product.handle}"
           class="predictive-search__item predictive-search__item--link link link--text"
           tabindex="-1">
          ${imgTag}
          <div class="predictive-search__item-content">
            <h3 class="predictive-search__item-heading h5">${product.title}</h3>
            ${priceHTML}
          </div>
        </a>
      </li>`;
  }
  // ─────────────────────────────────────────────────────────────────────

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);